import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings as SettingsIcon, User, Building2, Globe, Phone,
  Save, Lock, Eye, EyeOff, Shield, RefreshCw, Check, AlertCircle,
  Image, Gauge, Wallet, Crown, Upload, X, MapPin, FileText, Mail, Copy,
  Zap, Droplet, Webhook, Link2, ShoppingCart
} from 'lucide-react';
import { partnerAPI, authAPI, advancedSettingsAPI, subscriptionAPI } from '../../api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

// Subscription tier order
const TIER_ORDER = { starter: 0, growth: 1, professional: 2, enterprise: 3 };

// Section wrapper with subscription-based blur
const SettingsSection = ({ 
  title, 
  description,
  icon: Icon, 
  children, 
  requiredPlan = null,
  currentPlan = 'starter',
  iconColor = 'text-blue-600',
  className = ''
}) => {
  const isLocked = requiredPlan && TIER_ORDER[currentPlan] < TIER_ORDER[requiredPlan];
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${className}`}
    >
      {/* Section Header */}
      <div className="p-5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isLocked ? 'bg-slate-100' : 'bg-blue-50'}`}>
            <Icon className={`w-5 h-5 ${isLocked ? 'text-slate-400' : iconColor}`} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{title}</h3>
            {description && <p className="text-sm text-slate-500">{description}</p>}
          </div>
        </div>
        {isLocked && (
          <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
            <Crown className="w-3 h-3" /> {requiredPlan}+ Plan
          </span>
        )}
      </div>
      
      {/* Section Content */}
      <div className="relative">
        <div className={`p-5 ${isLocked ? 'filter blur-[3px] pointer-events-none select-none' : ''}`}>
          {children}
        </div>
        
        {/* Locked Overlay */}
        {isLocked && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Lock className="w-6 h-6 text-amber-600" />
              </div>
              <p className="text-slate-700 font-medium mb-1">Upgrade to {requiredPlan}</p>
              <p className="text-slate-500 text-sm mb-4">Unlock {title.toLowerCase()} features</p>
              <button
                onClick={() => navigate('/partners/subscription')}
                className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors"
              >
                View Plans
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Settings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isProfileCompletion = searchParams.get('complete_profile') === 'true';
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copiedPartnerId, setCopiedPartnerId] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    phone: '',
    website: '',
    address: '',
    gst_number: '',
    daily_credit_limit: 1000
  });

  // Get user info from localStorage
  const userStr = localStorage.getItem('nanokart_user');
  const user = userStr ? JSON.parse(userStr) : {};
  const partnerShortId = user?.partner_short_id || '';
  const partnerId = user?.partner_id || profile?.partner_id || '';

  // Advanced settings state
  const [advancedSettings, setAdvancedSettings] = useState({
    rate_limit_per_customer: '',
    rate_limit_per_sku: '',
    monthly_budget_limit: '',
    watermark_enabled: false,
    watermark_url: ''
  });
  const [canEditAdvanced, setCanEditAdvanced] = useState({
    rate_limits: false,
    watermark: false
  });
  const [savingAdvanced, setSavingAdvanced] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('starter');
  
  // Webhook settings state
  const [webhookSettings, setWebhookSettings] = useState({
    add_to_cart_webhook_url: '',
    webhook_url: '',
    callback_url: ''
  });
  const [savingWebhook, setSavingWebhook] = useState(false);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // OTP-based password change state
  const [passwordOtpStep, setPasswordOtpStep] = useState(0); // 0: choose method, 1: enter OTP, 2: enter new password
  const [passwordOtpData, setPasswordOtpData] = useState({ otp: '', new_password: '', confirm_password: '' });
  const [passwordOtpCountdown, setPasswordOtpCountdown] = useState(0);
  const [passwordChangeMethod, setPasswordChangeMethod] = useState('current'); // 'current' or 'otp'

  // Phone change state
  const [phoneChangeData, setPhoneChangeData] = useState({
    new_phone: '',
    otp: ''
  });
  const [phoneChangeStep, setPhoneChangeStep] = useState(0);
  const [changingPhone, setChangingPhone] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);

  // Logo upload state
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchAdvancedSettings();
  }, []);

  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCountdown]);

  useEffect(() => {
    if (passwordOtpCountdown > 0) {
      const timer = setTimeout(() => setPasswordOtpCountdown(passwordOtpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [passwordOtpCountdown]);

  const fetchProfile = async () => {
    try {
      const response = await partnerAPI.getProfile();
      const data = response.data;
      setProfile(data);
      setFormData({
        business_name: data.business_name || '',
        contact_name: data.contact_name || '',
        phone: data.phone || '',
        website: data.website || '',
        address: data.address || '',
        gst_number: data.gst_number || '',
        daily_credit_limit: data.daily_limit || 1000
      });
      setCurrentPlan((data.subscription_plan || 'starter').toLowerCase());
      setLogoUrl(data.logo_url || null);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvancedSettings = async () => {
    try {
      const response = await advancedSettingsAPI.get();
      const { settings = {}, can_edit, current_plan } = response.data || {};
      setAdvancedSettings({
        rate_limit_per_customer: settings?.rate_limit_per_customer || '',
        rate_limit_per_sku: settings?.rate_limit_per_sku || '',
        monthly_budget_limit: settings?.monthly_budget_limit || '',
        watermark_enabled: settings?.watermark_enabled || false,
        watermark_url: settings?.watermark_url || ''
      });
      // Also set webhook settings from the same response
      setWebhookSettings({
        add_to_cart_webhook_url: response.data?.add_to_cart_webhook_url || '',
        webhook_url: response.data?.webhook_url || '',
        callback_url: response.data?.callback_url || ''
      });
      setCanEditAdvanced(can_edit || false);
      if (current_plan) setCurrentPlan(current_plan.toLowerCase());
    } catch (error) {
      console.error('Failed to load advanced settings:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAdvancedChange = (field, value) => {
    setAdvancedSettings({ ...advancedSettings, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.contact_name || !formData.phone) {
      toast.error('Please fill in all mandatory fields (Business Name, Contact Name, Phone)');
      return;
    }
    
    setSaving(true);
    try {
      await partnerAPI.updateProfile(formData);
      toast.success('Settings saved successfully!');
      
      if (isProfileCompletion) {
        navigate('/partners', { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAdvanced = async () => {
    setSavingAdvanced(true);
    try {
      const dataToSave = {};
      if (advancedSettings.rate_limit_per_customer) {
        dataToSave.rate_limit_per_customer = parseInt(advancedSettings.rate_limit_per_customer);
      }
      if (advancedSettings.rate_limit_per_sku) {
        dataToSave.rate_limit_per_sku = parseInt(advancedSettings.rate_limit_per_sku);
      }
      if (advancedSettings.monthly_budget_limit) {
        dataToSave.monthly_budget_limit = parseInt(advancedSettings.monthly_budget_limit);
      }
      dataToSave.watermark_enabled = advancedSettings.watermark_enabled;
      if (advancedSettings.watermark_url) {
        dataToSave.watermark_url = advancedSettings.watermark_url;
      }
      
      await advancedSettingsAPI.update(dataToSave);
      toast.success('Settings saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingAdvanced(false);
    }
  };

  const handleWebhookChange = (field, value) => {
    setWebhookSettings({ ...webhookSettings, [field]: value });
  };

  const handleSaveWebhook = async () => {
    setSavingWebhook(true);
    try {
      await advancedSettingsAPI.update({
        add_to_cart_webhook_url: webhookSettings.add_to_cart_webhook_url || null,
        webhook_url: webhookSettings.webhook_url || null,
        callback_url: webhookSettings.callback_url || null
      });
      toast.success('Webhook settings saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save webhook settings');
    } finally {
      setSavingWebhook(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await partnerAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // OTP-based password change handlers
  const handleSendPasswordOtp = async () => {
    setChangingPassword(true);
    try {
      const response = await partnerAPI.sendPasswordChangeOtp();
      toast.success(`OTP sent to phone ending in ${response.data.phone}`);
      setPasswordOtpStep(1);
      setPasswordOtpCountdown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleVerifyPasswordOtp = async () => {
    if (passwordOtpData.otp.length < 4) {
      toast.error('Please enter a valid OTP');
      return;
    }
    
    if (passwordOtpData.new_password !== passwordOtpData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordOtpData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await partnerAPI.verifyPasswordChangeOtp({
        otp: passwordOtpData.otp,
        new_password: passwordOtpData.new_password
      });
      toast.success('Password changed successfully!');
      setPasswordOtpStep(0);
      setPasswordOtpData({ otp: '', new_password: '', confirm_password: '' });
      setPasswordChangeMethod('current');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSendPhoneOtp = async () => {
    if (phoneChangeData.new_phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setChangingPhone(true);
    try {
      await partnerAPI.sendPhoneChangeOtp({ new_phone: phoneChangeData.new_phone });
      toast.success('OTP sent to new phone number');
      setPhoneChangeStep(2);
      setOtpCountdown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setChangingPhone(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    if (phoneChangeData.otp.length < 4) {
      toast.error('Please enter a valid OTP');
      return;
    }

    setChangingPhone(true);
    try {
      await partnerAPI.verifyPhoneChangeOtp({
        new_phone: phoneChangeData.new_phone,
        otp: phoneChangeData.otp
      });
      toast.success('Phone number updated successfully!');
      setPhoneChangeStep(0);
      setPhoneChangeData({ new_phone: '', otp: '' });
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setChangingPhone(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PNG or JPG image');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await partnerAPI.uploadLogo(formData);
      setLogoUrl(response.data.logo_url);
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Are you sure you want to delete your logo?')) return;
    
    setUploadingLogo(true);
    try {
      await partnerAPI.deleteLogo();
      setLogoUrl(null);
      toast.success('Logo deleted');
    } catch (error) {
      toast.error('Failed to delete logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isMissingMandatoryFields = !formData.business_name || !formData.contact_name || !formData.phone;

  const copyPartnerId = () => {
    const idToCopy = partnerShortId || partnerId;
    if (idToCopy) {
      navigator.clipboard.writeText(idToCopy);
      setCopiedPartnerId(true);
      setTimeout(() => setCopiedPartnerId(false), 2000);
      toast.success('Partner ID copied!');
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="settings-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <SettingsIcon className="w-6 h-6 text-blue-600" />
            Settings
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage your account, branding, and API configuration
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${
            currentPlan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
            currentPlan === 'professional' ? 'bg-blue-100 text-blue-700' :
            currentPlan === 'growth' ? 'bg-green-100 text-green-700' :
            'bg-slate-100 text-slate-600'
          }`}>
            <Crown className="w-3.5 h-3.5 inline mr-1" />
            {currentPlan} Plan
          </span>
        </div>
      </div>

      {/* Profile Completion Banner */}
      {(isProfileCompletion || isMissingMandatoryFields) && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-amber-900">Complete Your Profile</h3>
            <p className="text-amber-700 text-sm mt-1">
              Please fill in the mandatory fields to access all features.
            </p>
          </div>
        </div>
      )}

      {/* Partner ID Card */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-5 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Your Partner ID</p>
              <code className="text-xl font-mono font-bold">{partnerShortId || partnerId}</code>
            </div>
          </div>
          <button
            onClick={copyPartnerId}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Copy Partner ID"
          >
            {copiedPartnerId ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
          </button>
        </div>
        <p className="text-slate-400 text-xs mt-3">Use this ID for API integrations and support requests</p>
      </div>

      {/* ==================== SECTION 1: BASIC PROFILE ==================== */}
      <SettingsSection
        title="Business Profile"
        description="Your company and contact information"
        icon={Building2}
        currentPlan={currentPlan}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Business Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                placeholder="Your company name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Contact Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                placeholder="Your full name"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input
                type="email"
                value={profile?.email || ''}
                disabled
                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                placeholder="10-digit mobile number"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                placeholder="https://yourwebsite.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">GST Number</label>
              <input
                type="text"
                name="gst_number"
                value={formData.gst_number}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                placeholder="22AAAAA0000A1Z5"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Business Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
              placeholder="Full business address"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4" /> Save Profile</>
            )}
          </button>
        </form>
      </SettingsSection>

      {/* ==================== SECTION 2: BRANDING (Logo & Watermark) ==================== */}
      <SettingsSection
        title="Branding & Watermark"
        description="Customize your logo and output watermarks"
        icon={Image}
        iconColor="text-purple-600"
        requiredPlan="growth"
        currentPlan={currentPlan}
      >
        <div className="space-y-6">
          {/* Logo Upload */}
          <div>
            <h4 className="font-medium text-slate-900 mb-3">Business Logo</h4>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                {logoUrl ? (
                  <div className="relative group">
                    <img src={logoUrl} alt="Logo" className="w-20 h-20 rounded-xl object-cover border border-slate-200" />
                    <button
                      onClick={handleLogoDelete}
                      disabled={uploadingLogo}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                    <Image className="w-6 h-6 text-slate-400" />
                  </div>
                )}
              </div>
              <div>
                <label className="cursor-pointer">
                  <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                    uploadingLogo ? 'bg-slate-100 text-slate-400' : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                  }`}>
                    {uploadingLogo ? <div className="w-4 h-4 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" /> : <Upload className="w-4 h-4" />}
                    {logoUrl ? 'Change Logo' : 'Upload Logo'}
                  </span>
                  <input type="file" accept="image/png,image/jpeg" onChange={handleLogoUpload} disabled={uploadingLogo} className="hidden" />
                </label>
                <p className="text-xs text-slate-500 mt-2">PNG or JPG, max 2MB. Shows on public try-on pages.</p>
              </div>
            </div>
          </div>

          {/* Watermark Settings */}
          <div className="border-t border-slate-100 pt-5">
            <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
              <Droplet className="w-4 h-4 text-purple-600" />
              Output Watermark
            </h4>
            
            <div className="flex items-center gap-3 mb-4">
              <button
                type="button"
                onClick={() => handleAdvancedChange('watermark_enabled', !advancedSettings.watermark_enabled)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  advancedSettings.watermark_enabled ? 'bg-purple-600' : 'bg-slate-300'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  advancedSettings.watermark_enabled ? 'left-7' : 'left-1'
                }`} />
              </button>
              <span className="text-sm text-slate-700">
                {advancedSettings.watermark_enabled ? 'Watermarking enabled' : 'Watermarking disabled'}
              </span>
            </div>

            {advancedSettings.watermark_enabled && (
              <div>
                <label className="block text-sm text-slate-600 mb-1">Watermark Image URL</label>
                <input
                  type="url"
                  value={advancedSettings.watermark_url}
                  onChange={(e) => handleAdvancedChange('watermark_url', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  placeholder="https://your-domain.com/watermark.png"
                />
                <p className="text-xs text-slate-500 mt-1">PNG with transparency recommended. Max 200x200px.</p>
                
                {advancedSettings.watermark_url && (
                  <div className="mt-3 p-3 border border-slate-200 rounded-lg bg-slate-50 inline-block">
                    <p className="text-xs text-slate-500 mb-2">Preview:</p>
                    <img 
                      src={advancedSettings.watermark_url} 
                      alt="Watermark" 
                      className="max-w-[80px] max-h-[80px] object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            onClick={handleSaveAdvanced}
            disabled={savingAdvanced}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {savingAdvanced ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
            Save Branding
          </button>
        </div>
      </SettingsSection>

      {/* ==================== SECTION 3: RATE LIMITS ==================== */}
      <SettingsSection
        title="Rate Limits & Budget"
        description="Control API usage and spending limits"
        icon={Gauge}
        iconColor="text-orange-600"
        requiredPlan="professional"
        currentPlan={currentPlan}
      >
        <div className="space-y-5">
          {/* Per Customer Rate Limit */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Rate Limit per Customer
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={advancedSettings.rate_limit_per_customer}
                  onChange={(e) => handleAdvancedChange('rate_limit_per_customer', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                  placeholder="e.g., 100"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">calls/day</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">Max API calls per customer ID per day</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Rate Limit per SKU
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={advancedSettings.rate_limit_per_sku}
                  onChange={(e) => handleAdvancedChange('rate_limit_per_sku', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                  placeholder="e.g., 500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">calls/day</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">Max API calls per SKU ID per day</p>
            </div>
          </div>

          {/* Budget Limit */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              <Wallet className="w-4 h-4 inline mr-1 text-green-600" />
              Monthly Budget Limit
            </label>
            <div className="relative max-w-xs">
              <input
                type="number"
                value={advancedSettings.monthly_budget_limit}
                onChange={(e) => handleAdvancedChange('monthly_budget_limit', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                placeholder="e.g., 10000"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">credits</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">API will stop when monthly limit is reached. Leave empty for no limit.</p>
          </div>

          {/* Daily Credit Limit */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              <Zap className="w-4 h-4 inline mr-1 text-amber-500" />
              Daily Credit Limit
            </label>
            <div className="relative max-w-xs">
              <input
                type="number"
                name="daily_credit_limit"
                value={formData.daily_credit_limit}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                min="100"
                max="100000"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">credits</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Maximum credits that can be used per day</p>
          </div>

          <button
            onClick={handleSaveAdvanced}
            disabled={savingAdvanced}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {savingAdvanced ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
            Save Limits
          </button>
        </div>
      </SettingsSection>

      {/* ==================== SECTION 4: WEBHOOKS & INTEGRATIONS ==================== */}
      <SettingsSection
        title="Webhooks & Integrations"
        description="Configure webhook URLs for SDK integrations"
        icon={Webhook}
        iconColor="text-indigo-600"
        currentPlan={currentPlan}
      >
        <div className="space-y-6">
          {/* Add to Cart Webhook */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-green-600" />
              Add to Cart Webhook URL
            </label>
            <input
              type="url"
              value={webhookSettings.add_to_cart_webhook_url}
              onChange={(e) => handleWebhookChange('add_to_cart_webhook_url', e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              placeholder="https://your-store.com/api/webhook/add-to-cart"
            />
            <p className="text-xs text-slate-500 mt-1.5">
              When configured, the "Add to Cart" button will appear in the TryOn Studio SDK. 
              Your endpoint will receive cart data including product details, size, and try-on image URL.
            </p>
          </div>

          {/* Webhook Payload Example */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Example Webhook Payload
            </h4>
            <pre className="text-xs text-slate-600 bg-slate-100 rounded p-3 overflow-x-auto">
{`{
  "event": "add_to_cart",
  "timestamp": "2025-01-25T12:00:00Z",
  "partner_id": "abc123...",
  "session_id": "sess_xyz...",
  "customer_phone": "9876543210",
  "product": {
    "product_id": "prod_123",
    "product_name": "Blue Kurta",
    "sku_id": "SKU001",
    "size": "M",
    "quantity": 1,
    "price": 1999,
    "currency": "INR"
  },
  "tryon": {
    "tryon_image_url": "https://...",
    "garment_image_url": "https://..."
  }
}`}
            </pre>
          </div>

          {/* General Webhook URL */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-blue-600" />
              General Webhook URL (Optional)
            </label>
            <input
              type="url"
              value={webhookSettings.webhook_url}
              onChange={(e) => handleWebhookChange('webhook_url', e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="https://your-store.com/api/webhook"
            />
            <p className="text-xs text-slate-500 mt-1.5">
              Receive notifications for all events (try-on completions, moments generated, etc.)
            </p>
          </div>

          <button
            onClick={handleSaveWebhook}
            disabled={savingWebhook}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {savingWebhook ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
            Save Webhook Settings
          </button>
        </div>
      </SettingsSection>

      {/* ==================== SECTION 5: SECURITY ==================== */}
      <SettingsSection
        title="Security"
        description="Password and account security settings"
        icon={Shield}
        iconColor="text-red-600"
        currentPlan={currentPlan}
      >
        <div className="space-y-6">
          {/* Change Password */}
          <div>
            <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Change Password
            </h4>
            
            {/* Method Selection Tabs */}
            <div className="flex items-center gap-2 mb-4 p-1 bg-slate-100 rounded-lg w-fit">
              <button
                type="button"
                onClick={() => { setPasswordChangeMethod('current'); setPasswordOtpStep(0); }}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  passwordChangeMethod === 'current' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Use Current Password
              </button>
              <button
                type="button"
                onClick={() => { setPasswordChangeMethod('otp'); setPasswordOtpStep(0); }}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  passwordChangeMethod === 'otp' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Use OTP
              </button>
            </div>

            {/* Current Password Method */}
            {passwordChangeMethod === 'current' && (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-600 mb-1">Current Password</label>
                  <div className="relative max-w-md">
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(!showPasswords)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 max-w-md">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">New Password</label>
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      required
                      minLength={8}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Confirm Password</label>
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {changingPassword ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Lock className="w-4 h-4" />}
                  Change Password
                </button>
              </form>
            )}

            {/* OTP Method */}
            {passwordChangeMethod === 'otp' && (
              <div className="space-y-4 max-w-md">
                {/* Step 0: Send OTP */}
                {passwordOtpStep === 0 && (
                  <div>
                    <p className="text-sm text-slate-600 mb-4">
                      We'll send an OTP to your registered phone number ({profile?.phone ? `****${profile.phone.slice(-4)}` : 'not set'}) to verify it's you.
                    </p>
                    {profile?.phone ? (
                      <button
                        type="button"
                        onClick={handleSendPasswordOtp}
                        disabled={changingPassword}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {changingPassword ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Phone className="w-4 h-4" />}
                        Send OTP
                      </button>
                    ) : (
                      <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                        Please add a phone number first to use OTP-based password change.
                      </p>
                    )}
                  </div>
                )}

                {/* Step 1: Enter OTP and New Password */}
                {passwordOtpStep === 1 && (
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-50 rounded-lg flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                      <p className="text-sm text-blue-700">OTP sent to your registered phone</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-slate-600 mb-1">Enter OTP</label>
                      <input
                        type="text"
                        value={passwordOtpData.otp}
                        onChange={(e) => setPasswordOtpData({ ...passwordOtpData, otp: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-center text-lg font-semibold tracking-widest"
                        placeholder="----"
                        maxLength={4}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-slate-600 mb-1">New Password</label>
                      <div className="relative">
                        <input
                          type={showPasswords ? 'text' : 'password'}
                          value={passwordOtpData.new_password}
                          onChange={(e) => setPasswordOtpData({ ...passwordOtpData, new_password: e.target.value })}
                          className="w-full px-3 py-2 pr-10 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          placeholder="Min 8 characters"
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(!showPasswords)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-slate-600 mb-1">Confirm Password</label>
                      <input
                        type={showPasswords ? 'text' : 'password'}
                        value={passwordOtpData.confirm_password}
                        onChange={(e) => setPasswordOtpData({ ...passwordOtpData, confirm_password: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        placeholder="Confirm new password"
                      />
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => { setPasswordOtpStep(0); setPasswordOtpData({ otp: '', new_password: '', confirm_password: '' }); }}
                        className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleVerifyPasswordOtp}
                        disabled={changingPassword}
                        className="px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {changingPassword ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Check className="w-4 h-4" /> Verify & Change</>}
                      </button>
                    </div>
                    
                    <button
                      type="button"
                      onClick={handleSendPasswordOtp}
                      disabled={passwordOtpCountdown > 0 || changingPassword}
                      className="text-sm text-blue-600 hover:text-blue-700 disabled:text-slate-400 flex items-center gap-1"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      {passwordOtpCountdown > 0 ? `Resend in ${passwordOtpCountdown}s` : 'Resend OTP'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Change Phone */}
          <div className="border-t border-slate-100 pt-5">
            <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Phone Number
            </h4>
            
            <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 rounded-lg max-w-md">
              <span className="text-sm text-slate-600">Current:</span>
              <span className="font-medium text-slate-900">{profile?.phone || 'Not set'}</span>
            </div>

            {phoneChangeStep === 0 && (
              <button onClick={() => setPhoneChangeStep(1)} className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                Update phone number
              </button>
            )}

            {phoneChangeStep === 1 && (
              <div className="space-y-3 max-w-md">
                <div>
                  <label className="block text-sm text-slate-600 mb-1">New Phone Number</label>
                  <input
                    type="tel"
                    value={phoneChangeData.new_phone}
                    onChange={(e) => setPhoneChangeData({ ...phoneChangeData, new_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    placeholder="9876543210"
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setPhoneChangeStep(0)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button onClick={handleSendPhoneOtp} disabled={changingPhone} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                    {changingPhone ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Send OTP'}
                  </button>
                </div>
              </div>
            )}

            {phoneChangeStep === 2 && (
              <div className="space-y-3 max-w-md">
                <div className="p-3 bg-blue-50 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                  <p className="text-sm text-blue-700">OTP sent to {phoneChangeData.new_phone}</p>
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1">Enter OTP</label>
                  <input
                    type="text"
                    value={phoneChangeData.otp}
                    onChange={(e) => setPhoneChangeData({ ...phoneChangeData, otp: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-center text-lg font-semibold tracking-widest"
                    placeholder="----"
                    maxLength={4}
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setPhoneChangeStep(0); setPhoneChangeData({ new_phone: '', otp: '' }); }} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button onClick={handleVerifyPhoneOtp} disabled={changingPhone} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                    {changingPhone ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Check className="w-4 h-4" /> Verify</>}
                  </button>
                </div>
                <button onClick={handleSendPhoneOtp} disabled={otpCountdown > 0 || changingPhone} className="text-sm text-blue-600 hover:text-blue-700 disabled:text-slate-400 flex items-center gap-1">
                  <RefreshCw className="w-3.5 h-3.5" />
                  {otpCountdown > 0 ? `Resend in ${otpCountdown}s` : 'Resend OTP'}
                </button>
              </div>
            )}
          </div>
        </div>
      </SettingsSection>
    </div>
  );
};

export default Settings;
